import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {StorageService} from './storage.service';
import {UserService} from './user.service';

const GUEST_STORAGE_KEY = 'guest_crisis_mode';

/**
 * Crisis mode state for the current visitor.
 *
 * Members keep the flag on their account (`crisisModeEnabled`, toggled through
 * the API), guests keep it in local storage so crisis mode also works without
 * being logged in.
 */
@Injectable({
    providedIn: 'root'
})
export class CrisisModeService {

    private _active$ = new BehaviorSubject<boolean>(false);
    readonly active$ = this._active$.asObservable();

    private currentUser: any = null;
    private guestActive = false;
    private guestLoaded = false;

    constructor(
        private storageService: StorageService,
        private userService: UserService,
    ) {
        this.userService.data.subscribe((userData: any) => {
            this.currentUser = userData;
            this._active$.next(
                this.isMember(userData) ? !!userData.crisisModeEnabled : this.guestActive
            );
        });
    }

    get active(): boolean {
        return this._active$.getValue();
    }

    /**
     * Restore the guest flag from storage. Safe to call repeatedly.
     */
    async load(): Promise<void> {
        if (this.guestLoaded) return;

        this.guestActive = (await this.storageService.get(GUEST_STORAGE_KEY)) === true;
        this.guestLoaded = true;

        if (!this.isMember(this.currentUser)) {
            this._active$.next(this.guestActive);
        }
    }

    /**
     * Flip crisis mode for whoever is using the app right now.
     */
    async toggle(): Promise<void> {
        const token = await this.storageService.get('token');

        if (this.userService.isLoggedIn && token !== null) {
            await this.userService.toggleCrisisMode();
            // getAccount() re-emits the member data, which updates active$.
            await this.userService.getAccount();
            return;
        }

        this.guestActive = !this._active$.getValue();
        this.guestLoaded = true;
        this.storageService.set(GUEST_STORAGE_KEY, this.guestActive);
        this._active$.next(this.guestActive);
    }

    private isMember(userData: any): boolean {
        return !!userData && typeof userData === 'object' && Object.keys(userData).length > 0;
    }
}
